import React, { useEffect } from "react";
import { motion } from 'framer-motion';
import { Table, Download, Coins } from 'lucide-react';
import logo from '/img/logo.png';

export default function FeeStructure({ 
  accent = "#115e32",
  pageTitle = "Fee Structure | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  const bachelorSciEng = {
    title: 'Bachelor Program (Science and Engineering)',
    data: [
      ['Description', 'ME', 'CE', 'CSE', 'EEE', 'TE'],
      ['Total Credits', '166', '157', '163', '161', '154'],
      ['Admission Fee (once, non-refundable)', '16,500', '16,500', '16,500', '16,500', '16,500'],
      ['Application Form Fee', '500', '500', '500', '500', '500'],
      ['Registration Fee (Per Semester)', '5000', '5000', '4000', '4000', '4000'],
      ['Orientation Fee', '1000', '1000', '1000', '1000', '1000'],
      ['Tuition Fee (Per Credit)', '4600', '3400', '3400', '3400', '3200'],
      ['Development Fee', '1500', '1500', '1500', '1500', '1500'],
      ['Total Fee', '8,21,600', '5,91,800', '6,04,200', '5,97,400', '5,42,800'],
      ['After 50% Waiver', '4,39,800', '3,24,900', '3,27,100', '3,23,700', '2,96,400']
    ]
  };

  const bachelorArts = {
    title: 'Bachelor Program (School of Business, Arts & Science)',
    data: [
      ['Description', 'BBA', 'ELL', 'BIS','BHTM'],
      ['Total Credits', '131', '137', '142','145'],
      ['Admission Fee (once, non-refundable)', '16,500', '16,500', '16,500','16,500'],
      ['Application Form Fee', '500', '500', '500','500'],
      ['Registration Fee (Per Semester)', '3500', '3500', '3500','3500'],
      ['Orientation Fee', '1000', '1000', '1000','1000'],
      ['Tuition Fee (Per Credit)', '2700', '1700', '1200','4300'],
      ['Development Fee', '1500', '1500', '1500','1500'],
      ['Total Fee', '3,99,700', '2,78,900', '2,16,700','6,69,500'],
      ['After Waiver', '2,40,850 (45%)', '1,74,950 (45%)', '1,31,200 (50%)','3,57,750(50%)']
    ]
  };

  const masters = {
    title: 'Masters Program',
    data: [
      ['Description', 'MBA (61 CH)', 'MBA (37 CH)', 'MPH', 'MIS'],
      ['Total Credits', '61', '37', '80', '64'],
      ['Admission Fee (once, non-refundable)', '16,500', '16,500', '16,500', '16,500'],
      ['Application Form Fee', '500', '500', '500', '500'],
      ['Registration Fee (Per Semester)', '3500', '3500', '3500', '3500'],
      ['Orientation Fee', '1000', '1000', '1000', '1000'],
      ['Tuition Fee (Per Credit)', '2500', '2500', '3000', '1400'],
      ['Development Fee', '1500', '1500', '1500', '1500'],
      ['Total Fee', '1,84,500', '1,17,500', '2,72,000', '1,21,600'],
      ['After 50% Waiver', '1,08,250', '71,250', '1,52,000', '76,800']
    ]
  };

  const renderTable = (section, idx) => (
    <motion.div key={idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg border-t-4 border-[#e6c56a]">
      <h3 className="text-xl font-bold text-[#014864] mb-4 flex items-center gap-2"><Table size={20} />{section.title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm md:text-base">
          <tbody>
            {section.data.map((row, i) => (
              <tr key={i} className={`${i === 0 ? 'bg-[#E6FAFE] font-semibold' : ''} ${i % 2 === 0 ? 'bg-[#D6F5FE]' : 'bg-[#F0FCFF]'} border-b border-gray-200`}>
                {row.map((cell, j) => (
                  <td key={j} className={`p-3 text-[#014864] ${j === 0 ? 'font-semibold text-[#7c5b0e]' : ''}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] py-16 px-4 md:px-12">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto bg-white/60 rounded-3xl shadow-2xl overflow-hidden mb-12">
        <div className="grid md:grid-cols-2 gap-6 items-center p-8 md:p-12 bg-gradient-to-r from-[#6BCCF2] to-teal-50">
          <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
            <img src={logo} alt="UCTC Logo" className="w-28 mb-4" />
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#014864] leading-tight mb-2">Fee Structure</h1>
            <p className="text-gray-700 max-w-lg">Explore detailed fee structures for all Bachelor and Master programs. UCTC ensures affordability and transparency with generous waiver options for deserving students.</p>

            <div className="mt-6 flex gap-4">
              <a href="#download" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#01364C] to-[#6BCCF2] text-white font-semibold shadow hover:scale-105 transition"><Download size={18} />Download PDF</a>
            </div>
          </motion.div>

          {/* Hero SVG Illustration */}
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1 }} className="relative">
            <svg viewBox="0 0 500 300" className="w-full h-full">
              <defs>
                <linearGradient id="coin" x1="0" x2="1">
                  <stop offset="0%" stopColor="#f9d976" />
                  <stop offset="100%" stopColor="#f39c12" />
                </linearGradient>
              </defs>

              {/* Coins animation */}
              <motion.circle cx="120" cy="140" r="24" fill="url(#coin)" animate={{ cy: [140, 130, 140] }} transition={{ duration: 2.8, repeat: Infinity }} />
              <motion.circle cx="260" cy="90" r="18" fill="url(#coin)" animate={{ cy: [90, 80, 90] }} transition={{ duration: 3.2, repeat: Infinity }} />
              <motion.circle cx="380" cy="200" r="22" fill="url(#coin)" animate={{ cy: [200, 185, 200] }} transition={{ duration: 2.6, repeat: Infinity }} />

              {/* Card outline */}
              <motion.rect x="80" y="60" width="340" height="180" rx="20" fill="#B7EAFC" stroke="#01364C" strokeWidth="2" animate={{ y: [0, -5, 0] }} transition={{ duration: 4, repeat: Infinity }} />

              <text x="250" y="150" textAnchor="middle" fill="#014864" fontSize="20" fontWeight="700">Affordable & Transparent</text>
            </svg>
          </motion.div>
        </div>
      </div>

      {/* Fee Tables */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 gap-10">
        {renderTable(bachelorSciEng, 0)}
        {renderTable(bachelorArts, 1)}
        {renderTable(masters, 2)}
      </div>

      {/* Footer Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="max-w-6xl mx-auto mt-12 bg-[#E6FAFE] border-t-4 border-[#e6c56a] rounded-2xl p-6 text-center shadow-md">
        <h3 className="text-xl font-semibold text-[#014864] mb-2 flex justify-center items-center gap-2"><Coins size={22} /> Financial Assistance</h3>
        <p className="text-gray-700 max-w-3xl mx-auto">UCTC offers tuition fee waivers and scholarships for outstanding academic performance and financially challenged students. For details, contact the Student Affairs or Accounts Office.</p>
      </motion.div>
    </section>
  );
}
